/**
 * P1 Funnel — Probetrainings-Strecke.
 *
 * Routen:
 *   crm.p1-pforzheim.de/funnelkids   Kids
 *   crm.p1-pforzheim.de/funnel       Erwachsene
 *
 * Gestaltung
 * ----------
 * Übernimmt die Marke von p1-pforzheim.de, damit Anzeige, Website und Funnel
 * wie ein Stück wirken. Die Werte sind von dort ausgelesen, nicht geschätzt:
 *
 *   Grundfläche  #0d0d0d
 *   Akzent       #ef7c23   (Icons, aktive Navigation)
 *
 * Zwei Themen: `dunkel` für den Kampfsportfunnel, `hell` für die Kids —
 * dort entscheidet die Mutter, und die sucht nicht „hart", sondern
 * „gut aufgehoben". Umgeschaltet wird über `thema` in definitionen.py.
 *   Überschrift  Anton, Laufweite 1.8px, Versalien
 *   Fließtext    Inter, weiß auf 62 % Deckkraft
 *   Karten       dunkel, 1px helle Kante, Radius 16px
 *
 * Ein Unterschied zur Website ist Absicht: Dort ist der Hauptknopf weiß
 * umrandet auf Schwarz. Im Funnel ist er orange gefüllt — hier zählt nicht
 * Zurückhaltung, sondern dass der nächste Schritt sofort ins Auge fällt.
 *
 * Bewegung: Blöcke unterhalb der Karte blenden beim Scrollen einmalig ein,
 * das Kopfbild zieht leicht mit. Die Frage-Karte wird NIE animiert — sie ist
 * die Aufgabe, und alles, was sie verzögert, kostet Abschlüsse.
 * `prefers-reduced-motion` schaltet beides vollständig ab.
 *
 * Drei Entscheidungen kommen weiterhin aus den Zahlen: In OneClickGym brechen
 * 58 % nach der ersten Frage ab, wer die zweite beantwortet, kommt zu 69 %
 * durch. Also Fortschritt und Restdauer ab Schritt 1 sichtbar, Belohnung
 * oben statt unten, Vertrauenszeile direkt an der ersten Frage.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSeitenaufruf } from '@/hooks/useSeitenaufruf';

const API = '';

/* Bestätigung (14.10.2026): Termin festhalten, in den Kalender legen, Route zum Studio. */
const STUDIO_ADRESSE = 'Hauptstraße 26, 75181 Pforzheim';

function terminAngaben(slots, terminId) {
  const t = (slots?.termine || []).find((x) => x.termin_id === terminId);
  const d = t ? new Date(t.start) : null;
  return {
    termin_id: terminId, kurs: slots?.termintyp?.name, start: t?.start || null,
    dauer_min: slots?.termintyp?.dauer_min || 60,
    tag: d ? d.toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: '2-digit', timeZone: 'Europe/Berlin' }) : '',
    uhrzeit: d ? d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Berlin' }) : '',
  };
}

function kalenderDatei(v) {
  const zeit = (d) => d.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
  const beginn = new Date(v.start);
  const ende = new Date(beginn.getTime() + (v.dauer_min || 60) * 60000);
  const ics = ['BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//P1 Performance Center//Probetraining//DE', 'BEGIN:VEVENT',
    `UID:${v.termin_id}@p1-pforzheim.de`, `DTSTAMP:${zeit(new Date())}`, `DTSTART:${zeit(beginn)}`, `DTEND:${zeit(ende)}`,
    `SUMMARY:Probetraining${v.kurs ? ` ${v.kurs}` : ''} · P1 Performance Center`,
    `LOCATION:P1 Performance Center\\, ${STUDIO_ADRESSE.replace(/,/g, '\\,')}`,
    'DESCRIPTION:Bequeme Sportsachen und etwas zu trinken mitbringen. Bitte 10 Minuten früher da sein.',
    'END:VEVENT', 'END:VCALENDAR'].join('\r\n');
  return `data:text/calendar;charset=utf-8,${encodeURIComponent(ics)}`;
}

/* ------------------------------------------------------------------ Helfer */

function herkunftAusUrl() {
  /* Baustein 1: Die Parameter werden beim ERSTEN Aufruf in sessionStorage
     gelegt und von dort gelesen — nicht erst beim Absenden. Wer von der
     Anzeige kommt, in der App navigiert und ohne Parameter zurueckkehrt,
     behaelt seine Herkunft (First Touch: einmal geschrieben, nie geaendert).
     Cookiefrei; stirbt mit dem Tab, wie die Sitzungs-ID des Trackings. */
  const SCHLUESSEL = 'p1_herkunft';
  const p = new URLSearchParams(window.location.search);
  const raus = {};
  for (const k of ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content',
                   'utm_term', 'fbclid', 'gclid', 'ttclid', 'ref']) {
    const v = p.get(k);
    if (v) raus[k] = v;
  }
  /* Kurzquellen der QR-Codes (?q=flyer-kids&st=ring): nur Kleinbuchstaben/
     Ziffern/Bindestrich, max. 32 Zeichen — alles andere kommt gar nicht erst
     in den Speicher. Die Whitelist prueft das Backend (herkunft.py). */
  for (const k of ['q', 'st']) {
    const v = (p.get(k) || '').toLowerCase();
    if (/^[a-z0-9-]{1,32}$/.test(v)) raus[k] = v;
  }
  try {
    const fruehere = JSON.parse(sessionStorage.getItem(SCHLUESSEL) || 'null') || {};
    /* Regeln aus dem Auftrag: ein Aufruf OHNE Parameter loescht nichts
       (fruehere Werte bleiben); ein Aufruf mit ANDEREM q gewinnt — der
       Nutzer hat einen zweiten Code gescannt. */
    const vereint = Object.keys(raus).length ? { ...fruehere, ...raus } : fruehere;
    if (Object.keys(vereint).length) sessionStorage.setItem(SCHLUESSEL, JSON.stringify(vereint));
    return vereint;
  } catch { /* privater Modus o. ae. — dann gilt die URL allein */ }
  return raus;
}

async function post(pfad, koerper) {
  const antwort = await fetch(API + pfad, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(koerper),
  });
  if (!antwort.ok) {
    const daten = await antwort.json().catch(() => ({}));
    const d = daten.detail;
    const fehler = new Error((typeof d === 'string' && d) || d?.nachricht || 'Da ist etwas schiefgegangen. Bitte nochmal versuchen.');
    fehler.detail = d;
    throw fehler;
  }
  return antwort.json();
}

const sekundenProSchritt = 5;

/* Handynummer wie im Backend (funnels/router.py handynummer_pruefen):
   beginnt mit 0 oder +49, nach Bereinigung 10-13 Ziffern. Die Pruefung hier
   ist nur fuer die sofortige Rueckmeldung — entscheidend bleibt der Server. */
function handynummerPruefen(roh) {
  const wert = (roh || '').trim();
  if (!wert) return 'Bitte Deine Handynummer eintragen';
  const b = wert.replace(/[\s/().-]/g, '');
  let ziffern;
  if (b.startsWith('+49')) ziffern = '0' + b.slice(3);
  else if (b.startsWith('0049')) ziffern = '0' + b.slice(4);
  else if (b.startsWith('0')) ziffern = b;
  else return 'Die Nummer muss mit 0 oder +49 beginnen (z. B. 0176 12345678)';
  if (!/^\d+$/.test(ziffern)) return 'Bitte nur Ziffern, Leerzeichen oder / eingeben';
  if (ziffern.length < 10 || ziffern.length > 13) return 'Das sieht nicht nach einer Handynummer aus — bitte prüfen (10–13 Ziffern)';
  if (ziffern.startsWith('00')) return 'Die Nummer muss mit 0 oder +49 beginnen (z. B. 0176 12345678)';
  return null;
}

function istTestaufruf() {
  try { return new URLSearchParams(window.location.search).get('test') === '1'; } catch { return false; }
}

/**
 * Einblenden beim Scrollen.
 *
 * Bewusst zurückhaltend: 18 px Versatz, 620 ms, einmalig. Apple bewegt
 * Inhalte beim Erscheinen, nicht dauerhaft — was mitscrollt und wackelt,
 * wirkt billig statt teuer.
 *
 * Zwei Regeln, die hier wichtiger sind als der Effekt:
 *   1. Die Frage-Karte wird NIE animiert. Sie ist die Aufgabe; alles, was
 *      sie verzögert, kostet Abschlüsse.
 *   2. `rootMargin` löst 60 px früher aus, damit der Inhalt schon steht,
 *      wenn er in den Blick kommt — sonst sieht man beim schnellen Scrollen
 *      leere Kästen.
 */
function useSichtbar(optionen = {}) {
  const ref = useRef(null);
  const [sichtbar, setSichtbar] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ||
        !('IntersectionObserver' in window)) {
      setSichtbar(true);
      return;
    }
    const beobachter = new IntersectionObserver(([eintrag]) => {
      if (eintrag.isIntersecting) { setSichtbar(true); beobachter.disconnect(); }
    }, { rootMargin: '0px 0px -60px 0px', threshold: 0.08, ...optionen });
    beobachter.observe(el);
    return () => beobachter.disconnect();
  }, []);   // eslint-disable-line react-hooks/exhaustive-deps

  return [ref, sichtbar];
}

/** Blendet seine Kinder ein, sobald sie in den Blick kommen. */
function Enthuellen({ children, verzoegerung = 0, als: Als = 'div' }) {
  const [ref, sichtbar] = useSichtbar();
  return (
    <Als ref={ref} className={`p1-auf${sichtbar ? ' sichtbar' : ''}`}
         style={{ transitionDelay: `${verzoegerung}ms` }}>
      {children}
    </Als>
  );
}

/**
 * Sanftes Mitziehen des Kopfbilds.
 *
 * Über requestAnimationFrame und nur transform — damit läuft es auf der GPU
 * und kostet kein Neuberechnen des Layouts. Ohne das ruckelt Parallax auf
 * älteren Android-Geräten sichtbar.
 */
function useParallax(staerke = 0.08) {
  const ref = useRef(null);
  useEffect(() => {
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    let laeuft = false;
    const zeichnen = () => {
      laeuft = false;
      const el = ref.current;
      if (!el) return;
      // Ohne scale: das frühere scale(1.12) hat 12 % vom Motiv weggezoomt,
      // zusätzlich zum Beschnitt durch object-fit. Den Spielraum fürs
      // Mitziehen liefert jetzt der Rahmen, der oben über den Kopfbereich
      // hinausragt (siehe .p1-heroRahmen) — dort wird er sauber abgeschnitten.
      const y = Math.min(window.scrollY, 400);
      el.style.transform = `translate3d(0, ${(y * staerke).toFixed(1)}px, 0)`;
    };
    const beiScroll = () => {
      if (!laeuft) { laeuft = true; requestAnimationFrame(zeichnen); }
    };
    zeichnen();
    window.addEventListener('scroll', beiScroll, { passive: true });
    return () => window.removeEventListener('scroll', beiScroll);
  }, [staerke]);
  return ref;
}

/**
 * Bild mit Platzhalter-Rückfall.
 *
 * Solange die echten Fotos fehlen, erscheint eine beschriftete Fläche statt
 * eines kaputten Bildsymbols. So sieht man beim Durchklicken sofort, welche
 * Motive noch gebraucht werden.
 */
function Bild({ src, label, klasse, verhaeltnis, sofort = false }) {
  // useState merkt sich nur den ERSTEN Wert. Der Kopfbereich rendert, bevor
  // die Funnel-Daten da sind — src ist dann noch leer, und das Bild galt
  // dauerhaft als kaputt. Ergebnis: das Hero-Bild erschien nie. Deshalb bei
  // jeder neuen Quelle zurücksetzen.
  const [fehlgeschlagen, setFehlgeschlagen] = useState(false);
  useEffect(() => { setFehlgeschlagen(false); }, [src]);
  const kaputt = !src || fehlgeschlagen;
  if (kaputt) {
    return (
      <div className={`p1-platzhalter ${klasse || ''}`} style={{ aspectRatio: verhaeltnis }}>
        <span>{label}</span>
      </div>
    );
  }
  // Das Kopfbild steht über der Falz und ist das größte Element der Seite —
  // es lazy zu laden verzögert genau den ersten Eindruck. Alles andere darf
  // warten, bis es in den Blick kommt.
  return <img className={klasse} src={src} alt=""
              loading={sofort ? 'eager' : 'lazy'}
              fetchpriority={sofort ? 'high' : undefined}
              style={{ aspectRatio: verhaeltnis }} onError={() => setFehlgeschlagen(true)} />;
}

/**
 * Kopfbild mit zwei Zuschnitten.
 *
 * Der Kopfbereich ist am Rechner ein breiter Streifen (bis 4:1), am Handy
 * fast quadratisch. Mit EINEM Bild schneidet `object-fit: cover` in die eine
 * oder andere Richtung fast alles weg — gemessen waren am Rechner 45 % und
 * am Handy 56 % des Motivs übrig. Deshalb liefert der Server je Motiv einen
 * breiten (2,5:1) und einen hohen Zuschnitt (4:3), und der Browser nimmt den
 * passenden. Kein Lazy-Loading: das ist das grösste Element über der Falz.
 */
function HeroBild({ hero }) {
  if (!hero) return null;
  const breit = typeof hero === 'string' ? hero : hero.breit;
  const hoch = typeof hero === 'string' ? hero : hero.hoch;
  return (
    <picture>
      <source media="(min-width: 860px)" srcSet={breit} />
      <img className="p1-heroBild" src={hoch} alt=""
           loading="eager" fetchpriority="high" />
    </picture>
  );
}

/* ================================================================= Haupt */

export default function FunnelSeite({ slug }) {
  useSeitenaufruf(slug === 'kids' ? 'funnelkids' : 'funnel');
  const [funnel, setFunnel] = useState(null);
  const [token, setToken] = useState(null);
  const [index, setIndex] = useState(0);
  const [antworten, setAntworten] = useState({});
  const [eingabe, setEingabe] = useState('');
  const [slots, setSlots] = useState(null);
  const [optIn, setOptIn] = useState(false);
  /* Kids v3 (14.10.2026): Termin VOR den Kontaktdaten — der Kalender merkt nur vor, gebucht wird mit dem Absenden
     der Kontaktdaten (Schritt mit `abschluss`). */
  const [vorgemerkt, setVorgemerkt] = useState(null);
  const [fertig, setFertig] = useState(null);
  const [fehler, setFehler] = useState(null);
  const [laedt, setLaedt] = useState(false);
  const feldRef = useRef(null);
  const kartenRef = useRef(null);

  useEffect(() => {
    let weg = false;
    (async () => {
      try {
        const a = await fetch(`${API}/api/public/funnel/${slug}`);
        if (!a.ok) throw new Error('Diese Seite gibt es nicht mehr.');
        const daten = await a.json();
        if (weg) return;
        setFunnel(daten);
        document.title = `${daten.titel} · P1 Performance Center`;
        const { token } = await post(`/api/public/funnel/${slug}/start`,
                                     { herkunft: herkunftAusUrl(), test: istTestaufruf() });
        if (weg) return;
        setToken(token);
        /* ?vorwahl=<wert>: Anzeige oder QR-Code kennt die Sportart schon.
           Dann ist Frage 1 beantwortet, bevor jemand tippen muss. */
        try {
          const vorwahl = new URLSearchParams(window.location.search).get('vorwahl');
          const erster = daten.schritte?.[0];
          if (vorwahl && erster?.typ === 'auswahl' && erster.optionen.some((o) => o.wert === vorwahl)) {
            await post(`/api/public/funnel/${slug}/schritt`, { token, schritt: erster.key, wert: vorwahl });
            if (!weg) { setAntworten({ [erster.key]: vorwahl }); setIndex(1); }
          }
        } catch { /* Vorwahl ist Komfort — ohne sie startet der Funnel normal */ }
      } catch (f) { if (!weg) setFehler(f.message); }
    })();
    return () => { weg = true; };
  }, [slug]);

  const schritt = funnel?.schritte?.[index] ?? null;
  const anzahl = funnel?.schritte?.length ?? 0;
  const gewaehlt = useMemo(() => {
    if (!funnel) return [];
    return funnel.schritte
      .filter((s) => s.typ === 'auswahl' && antworten[s.key])
      .map((s) => s.optionen.find((o) => o.wert === antworten[s.key])?.label)
      .filter(Boolean);
  }, [funnel, antworten]);
  const restSekunden = Math.max((anzahl - index) * sekundenProSchritt, 10);

  /* Auswertung "erreicht": jeder angezeigte Schritt wird gemeldet — die
     Statistik zaehlt angezeigt, nicht beantwortet. Fehlertolerant. */
  useEffect(() => {
    if (!token || !schritt?.key || index === 0) return;
    post(`/api/public/funnel/${slug}/gesehen`, { token, schritt: schritt.key }).catch(() => {});
  }, [token, slug, schritt?.key, index]);

  useEffect(() => {
    setEingabe(antworten[schritt?.key] ?? '');
    setFehler(null);
    if (schritt && !['auswahl', 'kalender'].includes(schritt.typ)) {
      setTimeout(() => feldRef.current?.focus(), 120);
    }
    if (index > 0) kartenRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [index, schritt?.key]);   // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!token || schritt?.typ !== 'kalender') return;
    setLaedt(true);
    const max = schritt.max_termine ? `&max_termine=${schritt.max_termine}` : '';
    fetch(`${API}/api/public/funnel/${slug}/slots?token=${token}&tage=${schritt.tage_voraus || 14}${max}`)
      .then((a) => (a.ok ? a.json() : a.json().then((d) => Promise.reject(new Error(d.detail)))))
      .then(setSlots)
      .catch((f) => setFehler(f.message))
      .finally(() => setLaedt(false));
  }, [token, schritt?.typ, slug]);   // eslint-disable-line react-hooks/exhaustive-deps

  const melden = useCallback(async (wert) => {
    if (!token) return;
    setFehler(null); setLaedt(true);
    try {
      await post(`/api/public/funnel/${slug}/schritt`, { token, schritt: schritt.key, wert });
      setAntworten((a) => ({ ...a, [schritt.key]: wert }));
      setIndex((i) => i + 1);
    } catch (f) { setFehler(f.message); } finally { setLaedt(false); }
  }, [slug, token, schritt]);

  const kontaktMelden = useCallback(async (felder) => {
    if (!token) return;
    setFehler(null); setLaedt(true);
    try {
      await post(`/api/public/funnel/${slug}/schritt`, { token, schritt: schritt.key, felder });
      setAntworten((a) => ({ ...a, ...felder }));
      if (schritt.abschluss) {
        if (!vorgemerkt) { setIndex((i) => Math.max(i - 1, 0)); setFehler('Bitte zuerst einen Termin wählen.'); return null; }
        const daten = await post(`/api/public/funnel/${slug}/abschluss`, { token, termin_id: vorgemerkt.termin_id, opt_in: optIn });
        setFertig(daten.abschluss);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return null;
      }
      setIndex((i) => i + 1);
      return null;
    } catch (f) {
      /* 422 mit Feldfehlern: die Maske zeigt sie inline */
      const detail = f.detail;
      if (detail && typeof detail === 'object' && detail.felder) return detail.felder;
      setFehler(f.message);
      return null;
    } finally { setLaedt(false); }
  }, [slug, token, schritt, vorgemerkt, optIn]);

  const buchen = useCallback(async (terminId) => {
    setFehler(null); setLaedt(true);
    try {
      const daten = await post(`/api/public/funnel/${slug}/abschluss`,
                               { token, termin_id: terminId, opt_in: optIn });
      setVorgemerkt(terminAngaben(slots, terminId));
      setFertig(daten.abschluss);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (f) { setFehler(f.message); } finally { setLaedt(false); }
  }, [slug, token, optIn, slots]);

  if (fehler && !funnel) return <Rahmen><Meldung art="fehler">{fehler}</Meldung></Rahmen>;
  if (!funnel) return <Rahmen><Skelett /></Rahmen>;

  if (fertig) {
    return (
      <Rahmen funnel={funnel} fortschritt={1}>
        <div className="p1-karte p1-erfolg">
          <div className="p1-haken">✓</div>
          <h2>{fertig.titel}</h2>
          <p>{fertig.text}</p>
          {(fertig.mitbringen || funnel.abschluss?.mitbringen)?.length > 0 && (
            <ul className="p1-mitbringen">
              {(fertig.mitbringen || funnel.abschluss.mitbringen).map((m) => <li key={m}>{m}</li>)}
            </ul>
          )}
          {vorgemerkt?.start && (
            <div className="p1-vorgemerkt" data-testid="funnel-termin-fertig">
              <strong>{vorgemerkt.tag}, {vorgemerkt.uhrzeit} Uhr</strong>
              <span>{vorgemerkt.kurs ? `${vorgemerkt.kurs} · ` : ''}{STUDIO_ADRESSE}</span>
            </div>
          )}
          <div className="p1-fertig-knoepfe">
            {vorgemerkt?.start && (
              <a className="p1-knopf" href={kalenderDatei(vorgemerkt)} download="p1-probetraining.ics"
                 data-testid="funnel-kalender">In den Kalender</a>
            )}
            <a className="p1-knopf p1-knopf--geist" target="_blank" rel="noreferrer" data-testid="funnel-route"
               href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(`P1 Performance Center, ${STUDIO_ADRESSE}`)}`}>
              Route planen
            </a>
          </div>
        </div>
        <Enthuellen><Ablauf funnel={funnel} /></Enthuellen>
        <Enthuellen verzoegerung={60}><Faq funnel={funnel} /></Enthuellen>
      </Rahmen>
    );
  }

  return (
    <Rahmen funnel={funnel} fortschritt={(index + 1) / anzahl}>
      <div className="p1-karte" ref={kartenRef}>
        <div className="p1-schrittkopf">
          {index > 0
            ? <button className="p1-zurueck" onClick={() => setIndex((i) => i - 1)}>‹ Zurück</button>
            : <span />}
          <span className="p1-zaehler">
            Schritt {index + 1} von {anzahl}
            {index === 0 && <> · ca. {restSekunden} Sekunden</>}
          </span>
        </div>

        {/* Gewählte Antworten sichtbar lassen: der Weg wirkt dadurch kürzer,
            und niemand fragt sich, ob die Auswahl angekommen ist. */}
        {gewaehlt.length > 0 && (
          <div className="p1-chips">
            {gewaehlt.map((w) => <span key={w} className="p1-chip">{w}</span>)}
          </div>
        )}

        <h2 className="p1-frage">{schritt.frage}</h2>
        {/* Beim Kontakt-Schritt steht der Hinweis als Microcopy unter den Feldern */}
        {schritt.hinweis && schritt.typ !== 'kontakt' && <p className="p1-hinweis">{schritt.hinweis}</p>}

        {schritt.typ === 'auswahl' && (
          <div className="p1-gitter" style={{ '--spalten': schritt.spalten }}>
            {schritt.kacheln && schritt.optionen.map((o, i) => (
              <button key={o.wert} className={`p1-kachel${o.bild ? '' : ' p1-kachel--text'}`} disabled={laedt} data-testid={`funnel-kachel-${o.wert}`}
                      style={{ animationDelay: `${i * 40}ms` }} onClick={() => melden(o.wert)}>
                {o.bild && <img src={o.bild} alt="" loading="eager" />}
                <span><strong>{o.label}</strong>{o.hinweis && <small>{o.hinweis}</small>}</span>
              </button>
            ))}
            {!schritt.kacheln && schritt.optionen.map((o, i) => (
              <button key={o.wert} className="p1-zeile" disabled={laedt}
                      style={{ animationDelay: `${i * 40}ms` }}
                      onClick={() => melden(o.wert)}>
                <span>{o.label}{o.hinweis && <small className="p1-optionHinweis">{o.hinweis}</small>}</span>
                <svg className="p1-chev" width="8" height="13" viewBox="0 0 8 13" aria-hidden="true">
                  <path d="M1.5 1.5 6.5 6.5 1.5 11.5" fill="none" stroke="currentColor"
                        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            ))}
            {schritt.hinweis_link && (
              <a className="p1-hinweisLink" href={schritt.hinweis_link.href}>{schritt.hinweis_link.text}</a>
            )}
          </div>
        )}

        {['text', 'email', 'telefon'].includes(schritt.typ) && (
          <form onSubmit={(e) => { e.preventDefault(); melden(eingabe.trim()); }}>
            <input
              ref={feldRef} className="p1-feld" value={eingabe}
              onChange={(e) => setEingabe(e.target.value)}
              placeholder={schritt.platzhalter || 'Eingabe…'}
              type={schritt.typ === 'email' ? 'email' : schritt.typ === 'telefon' ? 'tel' : 'text'}
              inputMode={schritt.typ === 'telefon' ? 'tel' : undefined}
              autoComplete={{ vorname: 'given-name', nachname: 'family-name',
                              email: 'email', telefon: 'tel' }[schritt.key] || 'off'}
              enterKeyHint="next" maxLength={200}
            />
            <button type="submit" className="p1-knopf p1-weiter"
                    disabled={laedt || (!eingabe.trim() && schritt.pflicht)}>
              {laedt ? 'Moment…' : 'Weiter →'}
            </button>
            {!schritt.pflicht && (
              <button type="button" className="p1-ueberspringen" onClick={() => melden('')}>
                Überspringen
              </button>
            )}
          </form>
        )}

        {schritt.typ === 'kontakt' && schritt.abschluss && vorgemerkt && (
          <div className="p1-vorgemerkt" data-testid="funnel-vorgemerkt">
            <strong>{vorgemerkt.tag}, {vorgemerkt.uhrzeit} Uhr</strong>
            <span>{vorgemerkt.kurs ? `${vorgemerkt.kurs} · ` : ''}Platz wird gehalten</span>
          </div>
        )}

        {schritt.typ === 'kontakt' && (
          <KontaktFormular schritt={schritt} laedt={laedt} vorhanden={antworten} absenden={kontaktMelden}
                           optIn={schritt.abschluss ? optIn : undefined} setOptIn={setOptIn}
                           optInText={schritt.abschluss && funnel.opt_in?.aktiv ? funnel.opt_in.text : null} />
        )}

        {schritt.typ === 'kalender' && (
          <Kalender slots={slots} laedt={laedt} optIn={optIn} setOptIn={setOptIn}
                    optInText={!schritt.vor_kontakt && funnel.opt_in?.aktiv ? funnel.opt_in.text : null}
                    buchen={schritt.vor_kontakt ? (terminId) => {
                      setVorgemerkt(terminAngaben(slots, terminId));
                      setIndex((i) => i + 1);
                    } : buchen}
                    knopf={schritt.knopf} />
        )}

        {fehler && <Meldung art="fehler">{fehler}</Meldung>}
        {index === 0 && !funnel.vertrauen && <Vertrauen />}
      </div>

      {/* Nur die Blöcke UNTER der Karte werden eingeblendet — die Karte
          selbst nie, siehe useSichtbar(). */}
      <Enthuellen><Beweis funnel={funnel} /></Enthuellen>
      <Enthuellen verzoegerung={60}><Ablauf funnel={funnel} /></Enthuellen>
      <Enthuellen verzoegerung={40}><Stimme funnel={funnel} /></Enthuellen>
      <Enthuellen verzoegerung={40}><Trainer funnel={funnel} /></Enthuellen>
      <Enthuellen verzoegerung={40}><Faq funnel={funnel} /></Enthuellen>
    </Rahmen>
  );
}

/* ============================================================== Bausteine */

function Beweis({ funnel }) {
  const b = funnel.beweis;
  if (!b?.aktiv) return null;
  return (
    <div className="p1-beweis">
      {b.punkte.map((p) => (
        <div key={p.text}><strong>{p.zahl}</strong><span>{p.text}</span></div>
      ))}
    </div>
  );
}

function Ablauf({ funnel }) {
  const a = funnel.ablauf;
  if (!a?.aktiv) return null;
  return (
    <section className="p1-block">
      <h3>{a.titel}</h3>
      <ol className="p1-ablauf">
        {a.schritte.map((s, i) => (
          <Enthuellen key={s.nummer} als="li" verzoegerung={i * 90}>
            <span className="p1-nummer">{s.nummer}</span>
            <div><strong>{s.titel}</strong><p>{s.text}</p></div>
          </Enthuellen>
        ))}
      </ol>
    </section>
  );
}

/* Echte Kundenstimmen. Ohne Einträge erscheint gar nichts — lieber kein
   Block als ein erfundener (§ 5b UWG). Für die Kids-Seite liegen noch keine
   Eltern-Stimmen vor; sobald sie in definitionen.py stehen, taucht der
   Bereich von selbst auf. */
function Stimme({ funnel }) {
  const s = funnel.stimmen;
  const eintraege = s?.eintraege || [];
  if (!s?.aktiv || eintraege.length === 0) return null;
  return (
    <section className="p1-stimmen">
      {s.titel && <h3 className="p1-stimmenTitel">{s.titel}</h3>}
      <div className="p1-stimmenListe">
        {eintraege.map((e, i) => (
          <figure className="p1-stimme" key={i}>
            <blockquote>
              <p>„{e.text}"</p>
              <figcaption>{e.wer}</figcaption>
            </blockquote>
          </figure>
        ))}
      </div>
    </section>
  );
}

function Trainer({ funnel }) {
  const t = funnel.trainer;
  if (!t?.aktiv) return null;
  return (
    <section className="p1-trainer">
      <Bild src={t.bild} label="Trainerfoto" klasse="p1-trainerBild" verhaeltnis="1/1" />
      <div className="p1-trainerText">
        <strong>{t.name}</strong>
        <span>{t.rolle}</span>
        {/* Zitat ist optional: für die Headcoach Frauentraining liegt keins
            vor, und ein erfundenes kommt nicht in Frage. */}
        {t.zitat && <p>„{t.zitat}"</p>}
      </div>
    </section>
  );
}

function Kalender({ slots, laedt, optIn, setOptIn, optInText, buchen, knopf }) {
  /* Mit `knopf` (Kids v2) waehlt der Tipp nur aus, gebucht wird ueber den
     Knopf darunter — ein versehentlicher Tipp bucht nichts. Ohne `knopf`
     bleibt es beim Direkt-Tipp der anderen Funnel. */
  const [gewaehlt, setGewaehlt] = useState(null);
  const gruppen = useMemo(() => {
    if (!slots?.termine) return [];
    const map = new Map();
    for (const t of slots.termine) {
      const d = new Date(t.start);
      const key = d.toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: '2-digit' });
      if (!map.has(key)) map.set(key, []);
      map.get(key).push({ ...t, datum: d });
    }
    return [...map.entries()];
  }, [slots]);

  if (laedt && !slots) return <Skelett zeilen={3} />;

  if (slots && slots.anzahl === 0) {
    return (
      <div className="p1-leer">
        <p>Gerade sind alle Plätze belegt. Schreib uns kurz — wir finden einen Termin für Dich.</p>
        <a className="p1-knopf p1-whatsapp" href="https://wa.me/4915560882292">
          Per WhatsApp schreiben
        </a>
      </div>
    );
  }

  return (
    <div>
      {slots?.termintyp && (
        <div className="p1-kursband">
          <strong>{slots.termintyp.name}</strong>
          <span>{slots.termintyp.dauer_min} Min.</span>
        </div>
      )}

      {gruppen.map(([tag, termine]) => (
        <div key={tag} className="p1-tagblock">
          <div className="p1-tag">{tag}</div>
          <div className="p1-gitter" style={{ '--spalten': 2 }}>
            {termine.map((t) => (
              <button key={t.termin_id} className={`p1-slot${knopf && gewaehlt === t.termin_id ? ' p1-slot--aktiv' : ''}`}
                      disabled={laedt} aria-pressed={knopf ? gewaehlt === t.termin_id : undefined}
                      onClick={() => (knopf ? setGewaehlt(t.termin_id) : buchen(t.termin_id))}>
                <strong>
                  {t.datum.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })} Uhr
                </strong>
                <small className={t.restplaetze <= 3 ? 'p1-knapp' : ''}>
                  {t.restplaetze <= 3 ? `nur noch ${t.restplaetze} frei` : `${t.restplaetze} Plätze frei`}
                </small>
              </button>
            ))}
          </div>
        </div>
      ))}

      {optInText && (
        <label className="p1-optin">
          <input type="checkbox" checked={optIn} onChange={(e) => setOptIn(e.target.checked)} />
          <span>{optInText}</span>
        </label>
      )}
      {knopf && (
        <button type="button" className="p1-knopf p1-weiter" disabled={laedt || !gewaehlt}
                onClick={() => gewaehlt && buchen(gewaehlt)} data-testid="funnel-sichern">
          {laedt ? 'Moment…' : knopf}
        </button>
      )}
    </div>
  );
}

/* Kontakt auf EINEM Screen (Kids v2): Vorname + Handynummer, E-Mail
   eingeklappt. Fehler stehen inline am Feld, nicht als Meldung unten. */
function KontaktFormular({ schritt, laedt, vorhanden, absenden, optIn, setOptIn, optInText }) {
  const [werte, setWerte] = useState(() => Object.fromEntries(
    (schritt.felder || []).map((f) => [f.key, vorhanden[f.key === 'telefon' ? 'telefon_roh' : f.key] || vorhanden[f.key] || ''])));
  const [fehler, setFehler] = useState({});
  const [aufgeklappt, setAufgeklappt] = useState(() => Boolean(vorhanden.email));
  const erstesRef = useRef(null);
  useEffect(() => { setTimeout(() => erstesRef.current?.focus(), 120); }, []);

  const setzen = (k, v) => { setWerte((w) => ({ ...w, [k]: v })); setFehler((f) => ({ ...f, [k]: null })); };
  const pruefen = () => {
    const f = {};
    for (const feld of schritt.felder || []) {
      const w = (werte[feld.key] || '').trim();
      if (feld.typ === 'telefon') { const e = handynummerPruefen(w); if (e) f[feld.key] = e; }
      else if (!w && feld.pflicht) f[feld.key] = 'Bitte ausfüllen';
      else if (w && feld.typ === 'email' && !/^[^@\s]+@[^@\s]+\.[a-zA-Z]{2,}$/.test(w)) f[feld.key] = 'Bitte eine gültige E-Mail-Adresse angeben';
    }
    return f;
  };
  const abschicken = async (e) => {
    e.preventDefault();
    const f = pruefen();
    if (Object.keys(f).length) { setFehler(f); return; }
    const server = await absenden(Object.fromEntries(Object.entries(werte).map(([k, v]) => [k, (v || '').trim()])));
    if (server) setFehler(server);
  };
  const pflichtOk = (schritt.felder || []).every((f) => !f.pflicht || (werte[f.key] || '').trim());

  return (
    <form onSubmit={abschicken} noValidate>
      {(schritt.felder || []).map((f, i) => {
        if (f.eingeklappt && !aufgeklappt) {
          return (
            <button key={f.key} type="button" className="p1-aufklappen" onClick={() => setAufgeklappt(true)}>
              {f.eingeklappt}
            </button>
          );
        }
        return (
          <label key={f.key} className="p1-feldgruppe">
            <span className="p1-feldlabel">{f.label}{!f.pflicht && <em> (optional)</em>}</span>
            <input
              ref={i === 0 ? erstesRef : undefined}
              className={`p1-feld${fehler[f.key] ? ' p1-feld--fehler' : ''}`}
              value={werte[f.key] || ''} onChange={(e) => setzen(f.key, e.target.value)}
              placeholder={f.platzhalter || ''}
              type={f.typ === 'email' ? 'email' : f.typ === 'telefon' ? 'tel' : 'text'}
              inputMode={f.typ === 'telefon' ? 'tel' : undefined}
              autoComplete={{ vorname: 'given-name', email: 'email', telefon: 'tel' }[f.key] || 'off'}
              enterKeyHint={i === (schritt.felder.length - 1) ? 'done' : 'next'} maxLength={200}
              aria-invalid={Boolean(fehler[f.key])} data-testid={`funnel-${f.key}`}
            />
            {fehler[f.key] && <span className="p1-feldfehler" role="alert">{fehler[f.key]}</span>}
          </label>
        );
      })}
      {optInText && (
        <label className="p1-optin">
          <input type="checkbox" checked={optIn} onChange={(e) => setOptIn(e.target.checked)} />
          <span>{optInText}</span>
        </label>
      )}
      {schritt.hinweis && <p className="p1-microcopy">{schritt.hinweis}</p>}
      <button type="submit" className="p1-knopf p1-weiter" disabled={laedt || !pflichtOk} data-testid="funnel-weiter">
        {laedt ? 'Moment…' : (schritt.knopf || 'Weiter →')}
      </button>
    </form>
  );
}

function Vertrauen() {
  return (
    <ul className="p1-vertrauen">
      <li>Antwort per WhatsApp</li>
      <li>Max. 16 Plätze pro Kurs</li>
      <li>Jederzeit absagbar</li>
    </ul>
  );
}

function Faq({ funnel }) {
  const [offen, setOffen] = useState(null);
  if (!funnel.faq?.length) return null;
  return (
    <section className="p1-faq">
      <h3>{funnel.faq_titel}</h3>
      {funnel.faq.map((f, i) => (
        <div key={i} className={`p1-faqEintrag ${offen === i ? 'offen' : ''}`}>
          <button onClick={() => setOffen(offen === i ? null : i)}>
            <span>{f.frage}</span>
            <svg className="p1-plus" width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
              <path d="M7 1v12M1 7h12" fill="none" stroke="currentColor"
                    strokeWidth="1.6" strokeLinecap="round"/>
            </svg>
          </button>
          {offen === i && <p>{f.antwort}</p>}
        </div>
      ))}
    </section>
  );
}

function Meldung({ art, children }) {
  return <p className={`p1-meldung p1-meldung--${art}`}>{children}</p>;
}

function Skelett({ zeilen = 2 }) {
  return (
    <div className="p1-karte">
      {Array.from({ length: zeilen }).map((_, i) => <div key={i} className="p1-skelett" />)}
    </div>
  );
}

function Rahmen({ funnel, fortschritt = 0, children }) {
  const heroRef = useParallax();
  return (
    <div className={`p1-seite p1-${funnel?.thema || 'dunkel'}`}>
      <Stile />
      <div className="p1-fortschritt"><div style={{ width: `${fortschritt * 100}%` }} /></div>

      <header className="p1-kopf">
        {/* Ohne Foto KEIN gestreifter Platzhalter: der ist ein Werkzeug fürs
            Durchklicken beim Bauen, auf einer öffentlichen Seite sieht er nur
            nach unfertig aus. Ohne Bild trägt die Markenfläche den Kopf —
            das wirkt gewollt. (Frauen-Funnel, bis das Foto da ist.) */}
        {funnel?.hero && (
          <div className="p1-heroRahmen" ref={heroRef}>
            <HeroBild hero={funnel.hero} />
          </div>
        )}
        <div className="p1-kopfInhalt">
          {funnel?.logo && <img className="p1-logo" src={funnel.logo} alt="P1 Performance Center" />}
          {funnel?.badge && <span className="p1-badge">{funnel.badge}</span>}
          {funnel?.titel && (
            <h1>
              {funnel.titel_akzent && funnel.titel.endsWith(funnel.titel_akzent)
                ? <>{funnel.titel.slice(0, -funnel.titel_akzent.length)}<span className="p1-akzent">{funnel.titel_akzent}</span></>
                : funnel.titel}
            </h1>
          )}
          {funnel?.unterzeile && <p className="p1-unter">{funnel.unterzeile}</p>}
          {funnel?.vertrauen?.length > 0 && (
            <ul className="p1-trust">
              {funnel.vertrauen.map((v) => <li key={v}>{v}</li>)}
            </ul>
          )}
          {funnel?.erfolg?.aktiv && funnel.erfolg.text && (
            <div className="p1-erfolgBlock">
              {funnel.erfolg.bild && <img src={funnel.erfolg.bild} alt="" loading="lazy" />}
              <p>{funnel.erfolg.text}</p>
            </div>
          )}
          {funnel?.bewertung?.aktiv && (
            <div className="p1-sterne">
              <span className="p1-sternGruppe" aria-hidden="true">★★★★★</span>
              {/* toFixed(1): sonst wird aus 5.0 die Anzeige „5" — Google
                  schreibt „5,0", und genau so soll es hier auch stehen. */}
              <span>{Number(funnel.bewertung.sterne).toFixed(1).replace('.', ',')} · {funnel.bewertung.anzahl} Bewertungen auf {funnel.bewertung.quelle}</span>
            </div>
          )}
        </div>
      </header>

      <main className="p1-inhalt">{children}</main>

      <footer className="p1-fuss">
        <a href="/impressum">Impressum</a>
        <a href="/datenschutz">Datenschutz</a>
      </footer>
    </div>
  );
}

/* ================================================================== Stile */

function Stile() {
  return (
    <style>{`
/* Anton und Inter kommen selbst gehostet aus public/fonts/fonts.css (global in
   index.html eingebunden). Der frühere @import auf fonts.googleapis.com wurde
   von der Content-Security-Policy ohnehin blockiert — und hätte die IP jedes
   Besuchers an Google gemeldet. */

/* ---------------------------------------------------------------------------
   Marken-Werte, ausgelesen von p1-pforzheim.de:
     Grundfläche  #0d0d0d
     Akzent       #ef7c23
     Überschrift  Anton, Laufweite 1.8px
     Fließtext    Inter, weiß auf 60 % Deckkraft
     Karten       dunkel, 1px helle Kante, Radius 16px
--------------------------------------------------------------------------- */
:root{
  --p1-bg:#0d0d0d; --p1-flaeche:#141414; --p1-erhoben:rgba(255,255,255,.055);
  --p1-kante:rgba(255,255,255,.12); --p1-kante-stark:rgba(255,255,255,.26);
  --p1-orange:#ef7c23; --p1-orange-hell:#f68f3f;
  --p1-weiss:#fff; --p1-text:rgba(255,255,255,.62); --p1-still:rgba(255,255,255,.42);
  --p1-r-karte:16px; --p1-r-feld:10px; --p1-r-pille:999px;
}
*{box-sizing:border-box}
.p1-seite{min-height:100vh;background:var(--p1-bg);color:var(--p1-weiss);
  font-family:Inter,-apple-system,system-ui,sans-serif;font-size:16px;line-height:1.55;
  -webkit-font-smoothing:antialiased}

.p1-fortschritt{position:fixed;inset:0 0 auto 0;height:3px;
  background:rgba(255,255,255,.09);z-index:50}
.p1-fortschritt>div{height:100%;background:var(--p1-orange);
  transition:width .45s cubic-bezier(.25,.1,.25,1)}

/* Kopf */
.p1-kopf{position:relative;overflow:hidden;isolation:isolate;background:#000}
.p1-heroBild{position:absolute;inset:0;width:100%;height:100%;object-fit:cover;
  opacity:.46;z-index:-2;filter:grayscale(.25) contrast(1.05)}
.p1-kopf::after{content:'';position:absolute;inset:0;z-index:-1;
  background:linear-gradient(180deg,rgba(13,13,13,.55) 0%,rgba(13,13,13,.72) 55%,var(--p1-bg) 100%)}
.p1-kopfInhalt{max-width:640px;margin:0 auto;padding:24px 22px 52px;text-align:center}
.p1-logo{height:30px;object-fit:contain;margin-bottom:20px;filter:brightness(0) invert(1)}
.p1-kopfInhalt h1{font-family:Anton,Impact,sans-serif;font-weight:400;
  font-size:clamp(32px,8.6vw,58px);line-height:1.02;letter-spacing:1.4px;
  margin:0;color:var(--p1-weiss);text-transform:uppercase;text-wrap:balance}
.p1-unter{margin:14px auto 0;max-width:34ch;color:var(--p1-text);font-size:15.5px}
.p1-sterne{display:inline-flex;align-items:center;gap:9px;margin-top:18px;
  border:1px solid var(--p1-kante);border-radius:var(--p1-r-pille);
  padding:7px 15px;color:var(--p1-weiss);font-size:12.5px}
.p1-sternGruppe{color:var(--p1-orange);letter-spacing:1px;font-size:12px}
.p1-inhalt{max-width:640px;margin:-34px auto 0;padding:0 22px 44px;position:relative}

/* Karte */
.p1-karte{background:var(--p1-flaeche);border:1px solid var(--p1-kante);
  border-radius:var(--p1-r-karte);padding:26px 22px 24px}
.p1-schrittkopf{display:flex;justify-content:space-between;align-items:center;
  gap:12px;margin-bottom:16px;min-height:22px}
.p1-zaehler{font-size:12.5px;color:var(--p1-still);letter-spacing:.04em;
  text-transform:uppercase}
.p1-zurueck{background:none;border:none;color:var(--p1-orange);font-size:14px;
  cursor:pointer;padding:2px 0;font-family:inherit}
.p1-zurueck:hover{color:var(--p1-orange-hell)}

.p1-chips{display:flex;flex-wrap:wrap;gap:6px;margin:0 0 16px}
.p1-chip{font-size:12px;color:var(--p1-text);border:1px solid var(--p1-kante);
  border-radius:var(--p1-r-pille);padding:4px 11px}
.p1-chip::before{content:'✓';color:var(--p1-orange);margin-right:6px;font-size:11px}

.p1-frage{font-family:Anton,Impact,sans-serif;font-weight:400;
  font-size:clamp(21px,5.4vw,28px);line-height:1.14;letter-spacing:.9px;
  margin:0 0 8px;text-transform:uppercase;color:var(--p1-weiss);text-wrap:balance}
.p1-hinweis{color:var(--p1-text);font-size:15px;margin:0 0 18px}
.p1-frage+.p1-gitter,.p1-frage+form,.p1-hinweis+form{margin-top:20px}

/* Auswahl */
.p1-gitter{display:grid;gap:9px;grid-template-columns:repeat(var(--spalten,1),1fr)}
.p1-kachel{display:flex;flex-direction:column;text-align:left;padding:0;overflow:hidden;cursor:pointer;border-radius:14px;
  border:1px solid var(--p1-kante);background:var(--p1-erhoben);color:var(--p1-weiss);font-family:inherit;
  animation:p1Auf .34s cubic-bezier(.25,.1,.25,1) both;transition:border-color .18s,transform .12s}
.p1-kachel img{width:100%;aspect-ratio:4/3;object-fit:cover;display:block}
.p1-kachel span{display:flex;flex-direction:column;padding:9px 11px 11px}
.p1-kachel strong{font-size:15px}.p1-kachel small{font-size:13px;opacity:.7;margin-top:1px}
.p1-kachel:hover:not(:disabled){border-color:var(--p1-orange)}.p1-kachel:active:not(:disabled){transform:scale(.98)}
.p1-kachel:disabled{opacity:.4;cursor:default}
/* Kachel ohne Bild: Name groß, Alter darunter, mittig */
.p1-kachel--text{justify-content:center;align-items:center;text-align:center;min-height:92px}
.p1-kachel--text span{align-items:center;padding:16px 10px}
.p1-kachel--text strong{font-size:18px}.p1-kachel--text small{font-size:14px;margin-top:3px}
.p1-vorgemerkt{display:flex;flex-direction:column;gap:2px;margin:14px 0 4px;padding:11px 13px;border-radius:12px;
  border:1.5px solid var(--p1-orange);background:rgba(239,124,35,.09)}
.p1-vorgemerkt span{font-size:13px;opacity:.75}
.p1-fertig-knoepfe{display:flex;flex-wrap:wrap;gap:10px;margin-top:14px}
.p1-fertig-knoepfe .p1-knopf{flex:1 1 160px;text-align:center}
.p1-zeile{display:flex;align-items:center;justify-content:space-between;gap:12px;
  width:100%;background:var(--p1-erhoben);border:1px solid var(--p1-kante);
  border-radius:var(--p1-r-feld);padding:17px 18px;font-size:16px;font-weight:500;
  color:var(--p1-weiss);text-align:left;cursor:pointer;font-family:inherit;
  animation:p1Auf .34s cubic-bezier(.25,.1,.25,1) both;
  transition:border-color .18s,background .18s,transform .12s}
.p1-zeile:hover:not(:disabled){border-color:var(--p1-orange);background:rgba(239,124,35,.09)}
.p1-zeile:active:not(:disabled){transform:scale(.99)}
.p1-zeile:disabled{opacity:.4;cursor:default}
.p1-zeile:focus-visible{outline:3px solid rgba(239,124,35,.55);outline-offset:2px}
.p1-chev{color:var(--p1-still);flex:0 0 auto;transition:transform .18s,color .18s}
.p1-zeile:hover .p1-chev{transform:translateX(3px);color:var(--p1-orange)}
@keyframes p1Auf{from{opacity:0;transform:translateY(7px)}to{opacity:1;transform:none}}

/* Knöpfe */
.p1-knopf{display:block;width:100%;text-align:center;text-decoration:none;
  background:var(--p1-orange);color:#fff;border:1px solid var(--p1-orange);
  border-radius:var(--p1-r-feld);padding:16px 26px;font-size:16px;font-weight:600;
  cursor:pointer;font-family:inherit;letter-spacing:.2px;
  transition:background .18s,transform .12s,border-color .18s}
.p1-knopf:hover:not(:disabled){background:var(--p1-orange-hell);border-color:var(--p1-orange-hell)}
.p1-knopf:active:not(:disabled){transform:scale(.985)}
.p1-knopf:disabled{opacity:.32;cursor:default}
.p1-knopf:focus-visible{outline:3px solid rgba(239,124,35,.55);outline-offset:2px}
.p1-knopf--geist{background:transparent;color:var(--p1-weiss);
  border-color:var(--p1-kante-stark);margin-top:22px}
.p1-knopf--geist:hover:not(:disabled){background:rgba(255,255,255,.06);
  border-color:var(--p1-weiss)}
.p1-whatsapp{background:#25d366;border-color:#25d366;color:#062b13;margin-top:16px}
.p1-whatsapp:hover:not(:disabled){background:#2ee071;border-color:#2ee071}
.p1-ueberspringen{display:block;margin:14px auto 0;background:none;border:none;
  color:var(--p1-still);font-size:14px;cursor:pointer;font-family:inherit;
  text-decoration:underline}
.p1-ueberspringen:hover{color:var(--p1-weiss)}

/* Kopf: Badge, Trust-Zeile, Beweis-Block (Kids v2) */
.p1-badge{display:inline-block;margin-bottom:12px;padding:6px 13px;border-radius:var(--p1-r-pille);
  background:var(--p1-orange);color:#fff;font-size:12.5px;font-weight:700;letter-spacing:.02em}
.p1-trust{list-style:none;margin:14px auto 0;padding:0;display:flex;flex-wrap:wrap;
  justify-content:center;gap:4px 14px;font-size:13px;color:var(--p1-text)}
.p1-trust li::before{content:'✓';color:var(--p1-orange);font-weight:700;margin-right:5px}
.p1-erfolgBlock{display:flex;align-items:center;gap:12px;margin:14px auto 0;max-width:520px;
  text-align:left;background:rgba(255,255,255,.06);border:1px solid var(--p1-kante);
  border-radius:var(--p1-r-feld);padding:10px 14px}
.p1-erfolgBlock img{width:52px;height:52px;border-radius:50%;object-fit:cover;flex:0 0 auto}
.p1-erfolgBlock p{margin:0;font-size:13.5px;line-height:1.45;color:var(--p1-weiss)}
.p1-optionHinweis{display:block;font-size:12.5px;font-weight:400;color:var(--p1-still);margin-top:2px}
.p1-akzent{color:var(--p1-orange)}
.p1-hinweisLink{display:block;text-align:center;margin-top:6px;padding:12px 8px;min-height:44px;
  color:var(--p1-text);font-size:14px;text-decoration:none}
.p1-hinweisLink:hover{color:var(--p1-orange)}

/* Kontakt-Formular */
.p1-feldgruppe{display:block;margin-bottom:12px}
.p1-feldlabel{display:block;font-size:13px;font-weight:600;color:var(--p1-text);margin-bottom:6px}
.p1-feldlabel em{font-style:normal;font-weight:400;color:var(--p1-still)}
.p1-feldgruppe .p1-feld{margin-bottom:0}
.p1-feld--fehler{border-color:#e5533d!important;box-shadow:0 0 0 3px rgba(229,83,61,.18)}
.p1-feldfehler{display:block;margin-top:6px;font-size:13px;color:#e5533d}
.p1-aufklappen{display:block;background:none;border:none;color:var(--p1-orange);font-size:14px;
  font-weight:600;cursor:pointer;font-family:inherit;padding:4px 0 14px;min-height:44px}
.p1-microcopy{margin:6px 0 14px;font-size:13px;color:var(--p1-still)}
.p1-slot--aktiv{border-color:var(--p1-orange)!important;background:rgba(239,124,35,.16)!important;
  box-shadow:0 0 0 2px var(--p1-orange) inset}
.p1-mitbringen{list-style:none;margin:18px auto 0;padding:14px 18px;max-width:34ch;text-align:left;
  border:1px dashed var(--p1-kante-stark);border-radius:var(--p1-r-feld)}
.p1-mitbringen li{font-size:15px;color:var(--p1-weiss);padding:3px 0}
.p1-mitbringen li::before{content:'✓';color:var(--p1-orange);font-weight:700;margin-right:8px}
/* Mindesthoehe 48 px fuer alle Tap-Ziele */
.p1-zeile,.p1-slot,.p1-knopf{min-height:48px}

/* Feld */
.p1-feld{width:100%;padding:16px;font-size:16.5px;border-radius:var(--p1-r-feld);
  border:1px solid var(--p1-kante);background:var(--p1-erhoben);color:var(--p1-weiss);
  margin-bottom:14px;font-family:inherit;transition:border-color .18s,box-shadow .18s}
.p1-feld::placeholder{color:var(--p1-still)}
.p1-feld:focus{outline:none;border-color:var(--p1-orange);
  box-shadow:0 0 0 3px rgba(239,124,35,.22)}

/* Kalender */
.p1-kursband{display:flex;justify-content:space-between;align-items:baseline;
  padding-bottom:14px;border-bottom:1px solid var(--p1-kante);font-size:15px}
.p1-kursband strong{font-family:Anton,sans-serif;font-weight:400;letter-spacing:.6px;
  text-transform:uppercase;font-size:16px}
.p1-kursband span{color:var(--p1-still);font-size:14px}
.p1-tagblock{margin-top:20px}
.p1-tag{font-size:12.5px;font-weight:600;color:var(--p1-still);margin-bottom:9px;
  text-transform:uppercase;letter-spacing:.06em}
.p1-slot{display:flex;flex-direction:column;gap:3px;align-items:center;
  background:var(--p1-erhoben);color:var(--p1-weiss);border:1px solid var(--p1-kante);
  border-radius:var(--p1-r-feld);padding:14px 8px;cursor:pointer;font-family:inherit;
  transition:border-color .18s,background .18s,transform .12s}
.p1-slot strong{font-family:Anton,sans-serif;font-weight:400;font-size:18px;letter-spacing:.5px}
.p1-slot small{font-size:12px;color:var(--p1-still)}
.p1-slot small.p1-knapp{color:var(--p1-orange);font-weight:600}
.p1-slot:hover:not(:disabled){border-color:var(--p1-orange);background:rgba(239,124,35,.09)}
.p1-slot:disabled{opacity:.4;cursor:default}
.p1-leer{text-align:center;color:var(--p1-text);font-size:15px}

.p1-optin{display:flex;gap:12px;align-items:flex-start;font-size:12.5px;
  color:var(--p1-still);line-height:1.45;margin-top:24px;padding-top:18px;
  border-top:1px solid var(--p1-kante);cursor:pointer}
.p1-optin input{margin-top:1px;width:17px;height:17px;flex:0 0 auto;accent-color:var(--p1-orange)}

/* Vertrauen */
.p1-vertrauen{list-style:none;display:flex;flex-wrap:wrap;gap:7px 18px;margin:22px 0 0;
  padding:16px 0 0;border-top:1px solid var(--p1-kante)}
.p1-vertrauen li{font-size:13px;color:var(--p1-still)}
.p1-vertrauen li::before{content:'✓';color:var(--p1-orange);margin-right:7px;font-weight:700}

/* Platzhalter */
.p1-platzhalter{display:grid;place-items:center;flex:0 0 auto;
  background:repeating-linear-gradient(45deg,#1a1a1a 0 12px,#212121 12px 24px);
  border:1px solid var(--p1-kante);color:var(--p1-still);font-size:11px;
  text-align:center;border-radius:var(--p1-r-feld);padding:8px}
.p1-heroBild.p1-platzhalter{position:absolute;inset:0;width:100%;height:100%;
  border:none;border-radius:0;
  background:repeating-linear-gradient(45deg,#141414 0 16px,#1b1b1b 16px 32px)}

/* Beweisstreifen */
.p1-beweis{display:grid;grid-template-columns:repeat(3,1fr);margin-top:12px;
  border:1px solid var(--p1-kante);border-radius:var(--p1-r-karte);overflow:hidden;
  background:var(--p1-flaeche)}
.p1-beweis>div{padding:20px 8px;text-align:center;border-left:1px solid var(--p1-kante)}
.p1-beweis>div:first-child{border-left:none}
.p1-beweis strong{display:block;font-family:Anton,sans-serif;font-weight:400;
  font-size:27px;letter-spacing:1px;color:var(--p1-orange)}
.p1-beweis span{display:block;font-size:12.5px;color:var(--p1-still);margin-top:3px}

/* Ablauf */
.p1-block{margin-top:12px;background:var(--p1-flaeche);border:1px solid var(--p1-kante);
  border-radius:var(--p1-r-karte);padding:24px 22px}
.p1-block h3{font-family:Anton,sans-serif;font-weight:400;font-size:19px;
  letter-spacing:.8px;text-transform:uppercase;margin:0 0 18px}
.p1-ablauf{list-style:none;margin:0;padding:0;display:grid;gap:18px}
.p1-ablauf li{display:flex;gap:14px;align-items:flex-start}
.p1-nummer{flex:0 0 auto;width:28px;height:28px;border-radius:50%;
  background:var(--p1-orange);color:#111;display:grid;place-items:center;
  font-size:14px;font-weight:700}
.p1-ablauf strong{display:block;font-size:15.5px;font-weight:600;color:var(--p1-weiss)}
.p1-ablauf p{margin:3px 0 0;color:var(--p1-text);font-size:14.5px;line-height:1.5}

/* Stimme & Trainer */
.p1-trainer{display:flex;gap:15px;align-items:flex-start;margin:12px 0 0;
  background:var(--p1-flaeche);border:1px solid var(--p1-kante);
  border-radius:var(--p1-r-karte);padding:22px}
.p1-trainerBild{width:58px;height:58px;border-radius:50%;object-fit:cover;font-size:9px}

.p1-stimmen{margin:12px 0 0}
.p1-stimmenTitel{margin:0 0 10px;font-family:Anton,sans-serif;font-weight:400;
  font-size:16px;letter-spacing:.8px;text-transform:uppercase;color:var(--p1-weiss)}
.p1-stimmenListe{display:grid;gap:10px}
@media (min-width:720px){.p1-stimmenListe{grid-template-columns:1fr 1fr}}
.p1-stimme{margin:0;background:var(--p1-flaeche);border:1px solid var(--p1-kante);
  border-radius:var(--p1-r-karte);padding:20px 22px;position:relative}
/* Anführungszeichen als stilles Zitatzeichen, nicht als Deko-Ballast */
.p1-stimme::before{content:"„";position:absolute;top:6px;right:16px;
  font-family:Anton,sans-serif;font-size:44px;line-height:1;
  color:var(--p1-orange);opacity:.22}
.p1-stimme blockquote{margin:0;min-width:0}
.p1-stimme p{margin:0;font-size:15px;line-height:1.55;color:var(--p1-weiss)}
.p1-stimme figcaption{margin-top:10px;color:var(--p1-still);font-size:13px;
  display:flex;align-items:center;gap:6px}
.p1-stimme figcaption::before{content:"";width:16px;height:1px;
  background:var(--p1-orange);flex:none}
.p1-trainerText{flex:1 1 auto;min-width:0}
.p1-trainerText strong{display:block;font-family:Anton,sans-serif;font-weight:400;
  font-size:17px;letter-spacing:.5px;text-transform:uppercase}
.p1-trainerText>span{display:block;color:var(--p1-orange);font-size:12.5px;margin-top:3px}
.p1-trainerText p{margin:11px 0 0;color:var(--p1-text);font-size:14.5px;line-height:1.55}

/* Erfolg */
.p1-erfolg{text-align:center;padding-top:32px}
.p1-erfolg h2{font-family:Anton,sans-serif;font-weight:400;font-size:26px;
  letter-spacing:1px;text-transform:uppercase;margin:18px 0 10px}
.p1-erfolg p{color:var(--p1-text);line-height:1.55;margin:0 auto;max-width:34ch;font-size:15.5px}
.p1-haken{width:58px;height:58px;border-radius:50%;background:var(--p1-orange);
  color:#111;font-size:26px;font-weight:700;display:grid;place-items:center;margin:0 auto;
  animation:p1Puls .5s cubic-bezier(.25,.1,.25,1) both}
@keyframes p1Puls{from{transform:scale(.7);opacity:0}to{transform:none;opacity:1}}

/* FAQ */
.p1-faq{margin-top:12px;background:var(--p1-flaeche);border:1px solid var(--p1-kante);
  border-radius:var(--p1-r-karte);padding:6px 22px 4px}
.p1-faq h3{font-family:Anton,sans-serif;font-weight:400;font-size:18px;letter-spacing:.8px;
  text-transform:uppercase;margin:20px 0 4px}
.p1-faqEintrag{border-top:1px solid var(--p1-kante)}
.p1-faqEintrag:first-of-type{border-top:none}
.p1-faqEintrag>button{width:100%;background:none;border:none;padding:18px 0;
  font-size:15px;font-weight:500;text-align:left;cursor:pointer;display:flex;
  justify-content:space-between;align-items:center;gap:16px;font-family:inherit;
  color:var(--p1-weiss);line-height:1.4}
.p1-plus{color:var(--p1-still);flex:0 0 auto;transition:transform .22s,color .22s}
.p1-faqEintrag.offen .p1-plus{transform:rotate(135deg);color:var(--p1-orange)}
.p1-faqEintrag>p{margin:0;padding:0 0 19px;color:var(--p1-text);font-size:14.5px;
  line-height:1.6;max-width:56ch}

/* Meldung, Skelett, Fuß */
.p1-meldung{font-size:14.5px;margin:16px 0 0;padding:12px 14px;
  border-radius:var(--p1-r-feld);text-align:center}
.p1-meldung--fehler{background:rgba(239,68,50,.13);color:#ff8b7a;
  border:1px solid rgba(239,68,50,.3)}
.p1-skelett{height:15px;border-radius:7px;margin-bottom:14px;
  background:linear-gradient(90deg,#1a1a1a 25%,#242424 50%,#1a1a1a 75%);
  background-size:200% 100%;animation:p1Schimmer 1.4s infinite}
@keyframes p1Schimmer{from{background-position:200% 0}to{background-position:-200% 0}}
.p1-fuss{max-width:640px;margin:0 auto;display:flex;gap:24px;justify-content:center;
  padding:0 22px 40px}
.p1-fuss a{color:var(--p1-still);font-size:13px;text-decoration:none}
.p1-fuss a:hover{color:var(--p1-weiss)}

@media(min-width:700px){
  .p1-kopfInhalt{padding:40px 22px 68px}
  .p1-logo{height:36px}
  .p1-karte,.p1-block,.p1-faq{padding-left:34px;padding-right:34px}
  .p1-inhalt{margin-top:-44px}
  /* Je breiter der Bildschirm, desto flacher wurde der Kopfbereich — bei
     1440px war er 4:1 und zeigte noch 45 % des Motivs. Mit dieser Mindest-
     höhe bleibt er nahe am Bildformat, ohne die Frage-Karte zu weit nach
     unten zu drücken. Der Inhalt sitzt dabei mittig. */
  .p1-kopf{min-height:clamp(400px,34vw,540px);
    display:flex;flex-direction:column;justify-content:center}
}
@media(prefers-reduced-motion:reduce){*{animation:none!important;transition:none!important}}
    
/* ===========================================================================
   Helles Thema — nur für den Kids-Funnel.

   Über das Probetraining entscheidet nicht das Kind, sondern die Mutter. Sie
   sucht nicht „hart", sondern „gut aufgehoben". Deshalb hier:
     - warmes Papierweiß statt Schwarz (#fdfaf6, nicht kaltes Grau)
     - Überschriften in Inter 700 statt Anton-Versalien; Anton bleibt nur bei
       den Kennzahlen, damit der Markenfaden nicht reißt
     - weiche Schatten statt harter Kanten, größere Radien
   Orange bleibt unverändert — auf Hell wirkt es warm statt aggressiv, und
   die Marke bleibt erkennbar.
   =========================================================================== */
.p1-hell{
  --p1-bg:#fdfaf6; --p1-flaeche:#fff; --p1-erhoben:#f6f1ea;
  --p1-kante:#ece4d9; --p1-kante-stark:#dbcfbf;
  --p1-weiss:#241c12; --p1-text:#6b5f52; --p1-still:#93867a;
  --p1-r-karte:20px; --p1-r-feld:14px;
  color:var(--p1-weiss)
}
.p1-hell .p1-fortschritt{background:rgba(0,0,0,.07)}

.p1-hell .p1-kopf{background:#efe7dc}
/* Das Kopfbild lag hier auf voller Deckkraft mit einer 6-%-Abdeckung — das
   ging nur auf, solange gar kein Bild geladen hat. Mit dem echten Foto stand
   dunkle Schrift auf dunklem Bildinhalt. Jetzt so viel Schleier, dass die
   Überschrift immer trägt, und so wenig, dass das Motiv erkennbar bleibt. */
.p1-hell .p1-heroBild{opacity:.9;filter:saturate(.94)}
.p1-hell .p1-kopf::after{
  background:linear-gradient(180deg,rgba(253,250,246,.58) 0%,rgba(253,250,246,.70) 48%,var(--p1-bg) 100%)}
.p1-hell .p1-kopfInhalt{padding-top:20px}
.p1-hell .p1-logo{filter:none}
.p1-hell .p1-kopfInhalt h1{
  font-family:Inter,system-ui,sans-serif;font-weight:700;text-transform:none;
  letter-spacing:-.022em;line-height:1.1;
  font-size:clamp(29px,7.6vw,46px);
  color:var(--p1-weiss);text-shadow:0 1px 18px rgba(253,250,246,.85)}
.p1-hell .p1-unter{color:#5a4f43;text-shadow:0 1px 12px rgba(253,250,246,.9)}
.p1-hell .p1-sterne{background:#fff;border-color:var(--p1-kante);
  color:var(--p1-weiss);box-shadow:0 2px 10px rgba(90,60,20,.09)}

.p1-hell .p1-karte,.p1-hell .p1-block,.p1-hell .p1-faq,
.p1-hell .p1-stimme,.p1-hell .p1-trainer,.p1-hell .p1-beweis{
  border-color:var(--p1-kante);box-shadow:0 6px 22px rgba(120,86,40,.07)}
.p1-hell .p1-stimmenTitel{color:var(--p1-tinte,#1a1a1a)}

.p1-hell .p1-frage{font-family:Inter,system-ui,sans-serif;font-weight:700;
  text-transform:none;letter-spacing:-.018em;line-height:1.2;
  font-size:clamp(21px,5.2vw,27px)}
.p1-hell .p1-zaehler{text-transform:none;letter-spacing:0;font-size:13.5px}
.p1-hell .p1-tag{text-transform:none;letter-spacing:0;font-size:14.5px;
  font-weight:600;color:var(--p1-weiss)}

.p1-hell .p1-zeile{background:var(--p1-erhoben);border-color:transparent;
  font-weight:600}
.p1-hell .p1-zeile:hover:not(:disabled){background:#fdf1e4;border-color:var(--p1-orange)}
.p1-hell .p1-feld{background:var(--p1-erhoben);border-color:transparent}
.p1-hell .p1-feld:focus{background:#fff;border-color:var(--p1-orange)}
.p1-hell .p1-slot{background:var(--p1-erhoben);border-color:transparent}
.p1-hell .p1-slot:hover:not(:disabled){background:#fdf1e4;border-color:var(--p1-orange)}
.p1-hell .p1-slot strong{font-family:Inter,sans-serif;font-weight:700;letter-spacing:-.01em}
.p1-hell .p1-kursband strong{font-family:Inter,sans-serif;font-weight:700;
  text-transform:none;letter-spacing:-.012em}

.p1-hell .p1-block h3,.p1-hell .p1-faq h3,.p1-hell .p1-erfolg h2,
.p1-hell .p1-trainerText strong{
  font-family:Inter,system-ui,sans-serif;font-weight:700;text-transform:none;
  letter-spacing:-.016em}
/* Markenfaden: die Kennzahlen bleiben Anton */
.p1-hell .p1-beweis strong{font-family:Anton,sans-serif}

.p1-hell .p1-nummer{color:#fff}
.p1-hell .p1-haken{color:#fff}
.p1-hell .p1-knopf{color:#fff}
.p1-hell .p1-knopf--geist{color:var(--p1-weiss);border-color:var(--p1-kante-stark)}
.p1-hell .p1-knopf--geist:hover:not(:disabled){background:var(--p1-erhoben)}
.p1-hell .p1-whatsapp{color:#06331a}
.p1-hell .p1-chip{background:var(--p1-erhoben);border-color:transparent;color:#5a4f43}
.p1-hell .p1-erfolgBlock{background:#fff;box-shadow:0 2px 10px rgba(90,60,20,.09)}
.p1-hell .p1-trust{color:#5a4f43}
.p1-hell .p1-badge{color:#fff}
.p1-hell .p1-platzhalter{
  background:repeating-linear-gradient(45deg,#f0e8dd 0 12px,#f7f1e8 12px 24px);
  border-color:var(--p1-kante);color:var(--p1-still)}
.p1-hell .p1-heroBild.p1-platzhalter{
  background:repeating-linear-gradient(45deg,#e9dfd1 0 16px,#f1e8dc 16px 32px)}
.p1-hell .p1-meldung--fehler{background:#fdeeea;color:#a33520;border-color:#f3d3c9}
.p1-hell .p1-skelett{
  background:linear-gradient(90deg,#f0e8dd 25%,#faf5ee 50%,#f0e8dd 75%);
  background-size:200% 100%}
    
/* --- Scroll-Animationen ---------------------------------------------------
   Kurve und Dauer sind der Punkt: 620 ms auf cubic-bezier(.16,1,.3,1) —
   schnell los, weich aus. Lineares oder federndes Einblenden wirkt sofort
   nach Baukasten. Bewegt wird ausschließlich opacity und transform, damit
   der Browser nichts neu berechnen muss.                                  */
.p1-auf{opacity:0;transform:translateY(18px);will-change:opacity,transform}
.p1-auf.sichtbar{opacity:1;transform:none;
  transition:opacity .62s cubic-bezier(.16,1,.3,1),transform .62s cubic-bezier(.16,1,.3,1)}
.p1-auf.sichtbar{will-change:auto}
li.p1-auf{display:flex;gap:14px;align-items:flex-start}

/* Kopfbild zieht beim Scrollen leicht mit. Der Rahmen bewegt sich, nicht das
   <img> — sonst kollidiert das mit dem Platzhalter-Fallback. */
/* Der Rahmen ragt 48px über den Kopfbereich hinaus. Das ist der Spielraum,
   den das Mitziehen beim Scrollen braucht — früher kam er aus scale(1.12),
   und der hat 12 % des Motivs weggezoomt. .p1-kopf schneidet sauber ab. */
.p1-heroRahmen{position:absolute;inset:-48px 0 0 0;z-index:-2;overflow:hidden}
.p1-heroRahmen .p1-heroBild{position:absolute;inset:0;width:100%;height:100%}
.p1-heroRahmen picture{display:contents}
    `}</style>
  );
}
